import { PlusSquareIcon, TrashIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  Autocomplete,
  GenericTable,
  ItemStatusEnum,
  Spinner,
  useApiQuery,
} from '@/modules/common';
import { StyleItem } from '@/modules/styles/types';

interface StylesSelectionProps {
  defaultValue: { styleId: string }[];
}

export function StylesSelection({ defaultValue }: StylesSelectionProps) {
  const { control, watch } = useFormContext();
  const { isFetching, data } = useApiQuery<StyleItem[]>('/styles', {
    queryKey: ['styles'],
    initialData: [],
  });

  const filteredData = useMemo(
    () => data.filter((item) => item.status === ItemStatusEnum.Ready),
    [data]
  );

  const formattedData = useMemo((): Record<string, StyleItem> => {
    if (!data) return {};

    return mapValues(keyBy(data, 'id'));
  }, [data]);

  const fields = watch('styles', defaultValue);
  const { append, remove } = useFieldArray({
    control,
    name: 'styles',
  });

  const filledStylesId = fields.map((style) => style.styleId).filter(Boolean);
  const stylesOptions = filteredData
    ?.filter((item) => !filledStylesId.includes(item.id))
    ?.map((style) => ({
      value: style.name,
      id: style.id,
    }));

  const columns: ColumnDef<StyleItem>[] = [
    {
      size: 200,
      accessorKey: 'name',
      header: 'Style Name',
      cell: ({ row }) => (
        <Autocomplete
          name={`styles.${row.index}.styleId`}
          control={control}
          searchable
          placeholder="Select style"
          options={stylesOptions}
          className="w-[200px] border-0"
          defaultValue={row.getValue('name')}
        />
      ),
    },
    {
      size: 100,
      accessorKey: 'imagePublicUrl',
      header: 'Preview',
      cell: ({ row }) =>
        row.getValue('imagePublicUrl') ? (
          <img
            key={row.getValue('imagePublicUrl')}
            src={row.getValue('imagePublicUrl')}
            alt="Preview"
            className="w-[30px] h-[34px] rounded-lg"
          />
        ) : null,
    },
    {
      accessorKey: 'positivePrompts',
      header: 'Positive Prompt',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('positivePrompts')}</span>
      ),
    },
    {
      accessorKey: 'negativePrompts',
      header: 'Negative Prompt',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('negativePrompts')}</span>
      ),
    },
    {
      size: 40,
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          data-testid="remove-style-button"
          onClick={() => remove(row.index)}
        >
          <TrashIcon size={28} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];

  const addNewEmptyStyle = () => {
    append({
      styleId: '',
      imagePublicUrl: '',
      positivePrompts: '',
      negativePrompts: '',
    });
  };

  const formattedStyles = fields.map((style) => {
    const formattedStyle = formattedData[style.styleId];
    return {
      ...style,
      id: formattedStyle?.id ?? '',
      name: formattedStyle?.name ?? '',
      imagePublicUrl: formattedStyle?.imagePublicUrl ?? '',
      positivePrompts: formattedStyle?.positivePrompts ?? '',
      negativePrompts: formattedStyle?.negativePrompts ?? '',
    };
  });

  return (
    <div>
      {isFetching ? (
        <Spinner />
      ) : (
        <>
          <GenericTable
            name="styles"
            key="styles-table"
            columns={columns}
            data={formattedStyles}
            emptyCellClassName="h-20"
            tableCellClassName="py-1"
            tableRowClassName="hover:bg-transparent"
            emptyDescription="No styles added. Please add new style."
          />

          {stylesOptions.length > 0 && (
            <Button
              disabled={isFetching}
              variant="transparent"
              className="mt-4"
              data-testid="add-style-button"
              onClick={addNewEmptyStyle}
            >
              <PlusSquareIcon />
              Add style
            </Button>
          )}
        </>
      )}
    </div>
  );
}
